/** Thin HTTP client wrapping fetch with JWT auth. */

const BASE = "/api/v1";

let _token: string | null = localStorage.getItem("token");
let _refreshing: Promise<boolean> | null = null;

export function setToken(token: string | null): void {
  _token = token;
  if (token) localStorage.setItem("token", token);
  else localStorage.removeItem("token");
}

export function getToken(): string | null {
  return _token;
}

/**
 * Attempt to obtain a new access token via the refresh-token cookie.
 * Returns true on success, false if the refresh token is also expired.
 */
async function tryRefresh(): Promise<boolean> {
  try {
    const res = await fetch(`${BASE}/auth/refresh`, {
      method: "POST",
      credentials: "include",
    });
    if (!res.ok) return false;
    const data = await res.json();
    if (data.access_token) {
      setToken(data.access_token);
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (_token) headers["Authorization"] = `Bearer ${_token}`;

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    credentials: "include",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401 || res.status === 403) {
    // Extract backend error detail
    const errBody = await res.json().catch(() => null);
    const detail = errBody?.detail;

    // If pending admin approval, surface error immediately — no refresh/redirect
    if (detail === "pending_approval") {
      throw new Error("pending_approval");
    }

    if (res.status === 401) {
      // Try silent refresh (only once at a time)
      if (!_refreshing) _refreshing = tryRefresh();
    const ok = await _refreshing;
    _refreshing = null;

    if (ok) {
      // Retry the original request with the new token
      const retryHeaders: Record<string, string> = { "Content-Type": "application/json" };
      if (_token) retryHeaders["Authorization"] = `Bearer ${_token}`;
      const retry = await fetch(`${BASE}${path}`, {
        method,
        headers: retryHeaders,
        credentials: "include",
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
      if (retry.ok) {
        if (retry.status === 204) return undefined as unknown as T;
        return retry.json() as Promise<T>;
      }
    }

    setToken(null);
    if (window.location.pathname !== "/login") {
      window.location.href = "/login";
    }
    throw new Error(detail || "Non autorisé");
    }

    // 403 for other reasons
    throw new Error(detail || "Accès interdit");
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail ?? `Erreur ${res.status}`);
  }

  if (res.status === 204) return undefined as unknown as T;
  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  del: <T>(path: string) => request<T>("DELETE", path),
};
